import * as React from "react";
import { useRef, useState } from "react";

import { CheckIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { useAtom } from "jotai";

import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { Separator } from "src/components/ui/separator";
import { cn } from "src/lib/utils";

/**
 * MetricsFilter component is a custom filter component for selecting multiple options.
 * It includes a button to open a popover with a list of options.
 * The selected options are displayed as badges.
 * The component uses Jotai for state management.
 *
 * @param {{
 *  title: string,
 *  options: { label: string, value: any, icon?: React.ElementType }[],
 *  field: string,
 *  atom: any,
 *  disabled?: boolean,
 *  queryVariableMapping?: (v: any) => any,
 *  onSubmit: () => void,
 * }} props
 * @returns {JSX.Element}
 */
export const MetricsFilter = ({
  title,
  icon: Icon = PlusCircledIcon,
  options,
  field,
  atom,
  disabled,
  queryVariableMapping = (v) => v,
  onSubmit,
}) => {
  const [open, setOpen] = useState(false);
  const [store, setStore] = useAtom(atom);

  const setValuesInStore = (newValues) => {
    setStore({
      ...store,
      [field]: newValues,
      ...(queryVariableMapping && {
        queryVariables: {
          ...store.queryVariables,
          [field]: queryVariableMapping(newValues),
        },
      }),
    });
  };

  const values = store[field] || [];
  const initialValueRef = useRef(null);

  return (
    <Popover
      open={open}
      onOpenChange={(state) => {
        setOpen(state);

        if (state) {
          initialValueRef.current = values;
          return;
        }

        // Only submit if value actually changed
        if (
          initialValueRef.current?.length !== values.length ||
          initialValueRef.current?.some((v) => !values.includes(v))
        ) {
          onSubmit?.();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 font-normal",
            !values.length
              ? "text-muted-foreground border-dashed shadow-none"
              : "text-foreground shadow-sm"
          )}
          disabled={disabled}
        >
          <Icon className="size-4" />
          {title}
          {values.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {values.length}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {values.length > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {values.length} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => values.includes(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = values.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newValues = [...values];
                      if (isSelected) {
                        newValues.splice(newValues.indexOf(option.value), 1);
                      } else {
                        newValues.push(option.value);
                      }

                      setValuesInStore(newValues);
                    }}
                  >
                    <div
                      className={cn(
                        "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className="text-muted-foreground mr-2 h-4 w-4" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {values?.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setValuesInStore([]);
                      onSubmit?.();

                      setOpen(false);
                    }}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
